/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, type Ref } from 'react';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import LinkMenuItem, {
	type LinkMenuItemWithAnalyticsProps,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { getTestId } from '../../utils/get-test-id/index.tsx';

export type Nav4MenuLinkItemProps<RouterLinkConfig extends Record<string, any> = never> = Omit<
	LinkMenuItemWithAnalyticsProps<RouterLinkConfig>,
	'isSelected'
> & {
	menuId: string;
};

/**
 * Nav4MenuLinkItem provides `menuId` instead of `isSelected`,
 * which is used to set the selected-state of the underlying
 * LinkMenuItem, using the useSidebarNav4() context-hook.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Nav4MenuLinkItem = forwardRef(Nav4MenuLinkItemContent) as <
	RouterLinkConfig extends Record<string, any> = never,
>(
	props: Nav4MenuLinkItemProps<RouterLinkConfig> & {
		ref?: Ref<HTMLAnchorElement>;
	},
) => ReturnType<typeof LinkMenuItem>;

function Nav4MenuLinkItemContent<RouterLinkConfig extends Record<string, any> = never>(
	props: Nav4MenuLinkItemProps<RouterLinkConfig>,
	ref: Ref<HTMLAnchorElement>,
) {
	const { menuId } = props;
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isSelectedPath } = useSidebarNav4();
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isControlledPath } = useSidebarNav4Controller();

	return (
		<LinkMenuItem<RouterLinkConfig>
			ref={ref}
			{...props}
			isSelected={isSelectedPath(menuId) || isControlledPath(menuId)}
			testId={getTestId(menuId)}
		/>
	);
}
